import React, { useState, useEffect, useRef } from 'react';
import { Check, ChevronsUpDown, Plus, Search, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { cn } from '../../lib/utils';
import { logger } from '../../lib/logger';

/**
 * SearchableSelect with Typeahead and Free Text Input
 * 
 * Features:
 * - Real-time search as user types
 * - Debounced API calls (300ms)
 * - Accepts free text input (no dropdown selection required)
 * - Auto-creates pending suggestions for new items
 * - "+ Add New Item" option when no results
 * - Keyboard navigation
 * - Customizable search endpoint
 */
export default function SearchableSelect({
  value,
  onValueChange,
  onSuggestNew,
  searchEndpoint,
  itemType = 'medication', // medication, lab_test, imaging_test, procedure
  placeholder = 'Select or type...',
  searchPlaceholder = 'Search or type new item...',
  emptyMessage = 'No results found',
  className,
  disabled = false,
  allowFreeText = true, // NEW: Allow accepting typed text directly
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const searchTimeoutRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`${searchEndpoint}?q=${encodeURIComponent(searchQuery)}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Search failed');
        }
        
        const data = await response.json();
        setSearchResults(data.results || []);
      } catch (error) {
        logger.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300); // 300ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, searchEndpoint]);

  const handleSelect = (item) => {
    setSelectedItem(item);
    onValueChange(item.name);
    setOpen(false);
    setSearchQuery('');
  };

  const handleAcceptFreeText = () => {
    if (!allowFreeText || !searchQuery.trim()) return;
    
    // Accept the typed text directly
    onValueChange(searchQuery.trim());
    setOpen(false);
    setSearchQuery('');
    
    // Auto-create suggestion in background (non-blocking)
    if (searchResults.length === 0 && searchQuery.trim().length >= 2) {
      // This will be handled by the parent component's onSuggestNew
      // We just accept the text, the parent will create the suggestion
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      e.preventDefault();
      if (searchResults.length > 0) {
        // If there are results, select the first one
        handleSelect(searchResults[0]);
      } else if (allowFreeText) {
        // If no results, accept the free text
        handleAcceptFreeText();
      }
    }
  };

  const handleSuggestNew = () => {
    setOpen(false);
    setSearchQuery('');
    if (onSuggestNew) {
      onSuggestNew(searchQuery);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          <span className="truncate">
            {value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <Input
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-9 p-0"
            autoFocus
          />
        </div>
        
        <div className="max-h-[300px] overflow-y-auto">
          {searching ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              <span className="ml-2 text-sm text-slate-500">Searching...</span>
            </div>
          ) : searchQuery.length >= 2 ? (
            searchResults.length > 0 ? (
              <div className="p-1">
                {searchResults.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSelect(item)}
                    className={cn(
                      "relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-slate-100 focus:bg-slate-100",
                      value === item.name && "bg-slate-100"
                    )}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === item.name ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex-1 text-left">
                      <div className="font-medium">{item.name}</div>
                      {item.description && (
                        <div className="text-xs text-slate-500 truncate">
                          {item.description}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
                
                {/* Accept Free Text Option */}
                {allowFreeText && (
                  <div className="border-t mt-1 pt-1 px-2 py-2">
                    <button
                      onClick={handleAcceptFreeText}
                      className="w-full flex items-center justify-center px-3 py-2 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-sm font-medium"
                    >
                      Use "{searchQuery}" (Press Enter)
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4">
                <p className="text-sm text-slate-500 text-center mb-3">{emptyMessage}</p>
                {allowFreeText && searchQuery.trim() && (
                  <Button
                    onClick={handleAcceptFreeText}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    size="sm"
                  >
                    Use "{searchQuery}" (Press Enter)
                  </Button>
                )}
              </div>
            )
          ) : (
            <div className="p-4 text-center text-sm text-slate-500">
              Type at least 2 characters to search
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
